import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Loader, Select, Text } from 'rizzui';
import { toast } from 'sonner';
import * as Yup from 'yup';
import useFetch from '../../hooks/useFetch';
import useHandleErrors from '../../hooks/useHandleErrors';
import { UserService } from '../../services/user/userService';
import { convertEditorJSONToHTML, convertHTMLToEditorJSON, isValidHTML } from '../../utils/editorConversions';
import ContractTemplateWYSIWYGEditor from '../contract-templates/ContractTemplateWYSIWYGEditor';
import { FileUpload } from '../FileUpload';

// Tipos para la configuración del formulario
interface EmailServiceConfig {
    sendEmail: (data: any) => Promise<any>;
}

interface SendEmailFormProps {
    onClose: () => void;
    entityType: 'client' | 'contract';
    entityId: string;
    service: EmailServiceConfig;
    title?: string;
    entityFieldName?: string; // Nombre del campo para el ID de la entidad (clientId, contractId, etc.)
}

// Schema de validación genérico
const createValidationSchema = (entityFieldName: string) => Yup.object({
    [entityFieldName]: Yup.string().optional(),
    emailSubject: Yup.string()
        .required('El asunto es obligatorio')
        .min(3, 'El asunto debe tener al menos 3 caracteres')
        .max(200, 'El asunto no puede exceder 200 caracteres'),
    emailContent: Yup.string()
        .required('El contenido es obligatorio')
        .min(10, 'El contenido debe tener al menos 10 caracteres'),
    documents: Yup.array()
        .of(Yup.mixed())
        .max(10, 'No se pueden adjuntar más de 10 archivos')
        .optional()
});

const SendEmailForm: React.FC<SendEmailFormProps> = ({
    onClose,
    entityType,
    entityId,
    service,
    entityFieldName = `${entityType}Id`
}) => {
    const { handleErrors } = useHandleErrors();
    const [selectedTemplate, setSelectedTemplate] = useState<{ id: string, template: string, name: string }>();
    const [organisingCorporationFilterId, setOrganisingCorporationFilterId] = useState<string>('');

    // Obtener plantillas filtradas por tipo de entidad
    const [templatesData] = useFetch(useCallback(async () => {
        const contextPayload = entityId
            ? (entityType === 'contract' ? { contractId: entityId } : { clientId: entityId })
            : {};

        const response = await (new UserService()).listEmailTemplates({
            page: 1,
            limit: 200,
            filter_filters: {
                use_type: entityType,
            },
            filter_order: [
                { field: 'name', order: 'asc' }
            ],
            applyContextOrganisingCorporations: Boolean(entityId),
            ...contextPayload,
        });
        return response.getResponseData();
    }, [entityId, entityType]));

    const templates = templatesData?.data || [];
    const contextHasOrganisingCorporations = Boolean(templatesData?.contextHasOrganisingCorporations);

    const organisingCorporationOptions = useMemo(() => {
        const mappedOrganisingCorporations = new Map<string, { value: string; label: string }>();

        templates.forEach((template: any) => {
            (template?.organisingCorporations || []).forEach((organisingCorporation: any) => {
                if (organisingCorporation?.id && organisingCorporation?.name) {
                    mappedOrganisingCorporations.set(organisingCorporation.id, {
                        value: organisingCorporation.id,
                        label: organisingCorporation.name
                    });
                }
            });
        });

        return Array.from(mappedOrganisingCorporations.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [templates]);

    const filteredTemplates = useMemo(() => {
        if (contextHasOrganisingCorporations || !organisingCorporationFilterId) {
            return templates;
        }

        return templates.filter((template: any) =>
            (template?.organisingCorporations || []).some((organisingCorporation: any) => organisingCorporation?.id === organisingCorporationFilterId)
        );
    }, [contextHasOrganisingCorporations, organisingCorporationFilterId, templates]);

    const templateOptions = useMemo(() => {
        return filteredTemplates.map((template: any) => {
            const organisingCorporationNames = (template?.organisingCorporations || [])
                .map((organisingCorporation: any) => organisingCorporation?.name)
                .filter(Boolean);

            const label = contextHasOrganisingCorporations
                ? template.name
                : `${template.name}${organisingCorporationNames.length > 0 ? ` (${organisingCorporationNames.join(', ')})` : ' (Sin organizadora)'}`;

            return {
                value: template.id,
                label
            };
        });
    }, [contextHasOrganisingCorporations, filteredTemplates]);

    useEffect(() => {
        if (!selectedTemplate?.id) {
            return;
        }

        const existsInFilteredList = filteredTemplates.some((template: any) => template.id === selectedTemplate.id);
        if (!existsInFilteredList) {
            setSelectedTemplate(undefined);
        }
    }, [filteredTemplates, selectedTemplate?.id]);

    // Procesar datos iniciales de la plantilla seleccionada
    const memoizedInitialData = useMemo(() => {
        if (!selectedTemplate) return undefined;
        const templateString = selectedTemplate.template as unknown as string;
        try {
            return JSON.parse(templateString);
        } catch {
            if (isValidHTML(templateString)) {
                return convertHTMLToEditorJSON(templateString);
            }
            return undefined;
        }
    }, [selectedTemplate]);

    // Función de envío genérica
    const handleSubmit = async (data: any) => {
        try {
            const response = await (await service.sendEmail(data)).getResponseData();
            if (response.success) {
                toast.success('Correo enviado correctamente');
                onClose();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            console.log(error);
            toast.error('Error al enviar el correo electrónico');
        }
    };

    // Configuración del formulario con Formik
    const formik = useFormik({
        initialValues: {
            [entityFieldName]: entityId,
            emailSubject: '',
            emailContent: '',
            documents: [],
        },
        validationSchema: createValidationSchema(entityFieldName),
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: handleSubmit,
    });

    // Configuración de etiquetas según el tipo de entidad
    const getEntityLabels = () => {
        switch (entityType) {
            case 'client':
                return {
                    title: 'Enviar correo electrónico al cliente',
                    templateLabel: 'Plantilla de cliente'
                };
            case 'contract':
                return {
                    title: 'Enviar correo electrónico del contrato',
                    templateLabel: 'Plantilla de contrato'
                };
            default:
                return {
                    title: 'Enviar correo electrónico',
                    templateLabel: 'Plantilla'
                };
        }
    };

    const labels = getEntityLabels();

    return (
        <div className='grid grid-cols-2 mb-6 gap-4 overflow-y-auto'>
            <Input
                label='Asunto'
                value={formik.values.emailSubject}
                onChange={(e) => formik.setFieldValue('emailSubject', e.target.value)}
                onBlur={() => formik.setFieldTouched('emailSubject', true)}
                error={formik.touched.emailSubject && formik.errors.emailSubject ? formik.errors.emailSubject : ''}
            />

            {!contextHasOrganisingCorporations && (
                <Select
                    label='Filtrar por organizadora'
                    value={organisingCorporationFilterId
                        ? organisingCorporationOptions.find((option) => option.value === organisingCorporationFilterId) || null
                        : null}
                    onChange={(e: any) => setOrganisingCorporationFilterId(e?.value || '')}
                    options={organisingCorporationOptions}
                    placeholder='Seleccione organizadora'
                    clearable
                />
            )}

            <Select
                label={labels.templateLabel}
                value={selectedTemplate ? { value: selectedTemplate?.id, label: selectedTemplate?.name } : null}
                onChange={(e: any) => {
                    const template = {
                        id: e.value,
                        name: e.label,
                        template: filteredTemplates.find((template: any) => template.id === e.value)?.templateContent || ''
                    };
                    setSelectedTemplate(template);
                    formik.setFieldValue('emailContent', template.template);
                }}
                options={templateOptions}
                placeholder='Seleccione una plantilla'
                disabled={!filteredTemplates.length}
            />

            <div className="col-span-2">
                <ContractTemplateWYSIWYGEditor
                    type='email'
                    initialData={memoizedInitialData}
                    title={'Contenido del correo electrónico'}
                    placeholder="Escriba aquí el contenido del correo electrónico..."
                    onChange={(data) => {
                        formik.setFieldValue('emailContent', convertEditorJSONToHTML(data));
                        formik.setFieldTouched('emailContent', true);
                    }}
                />
                {formik.touched.emailContent && formik.errors.emailContent && (
                    <Text className="text-red-500 text-sm mt-1">
                        {formik.errors.emailContent}
                    </Text>
                )}
            </div>

            {/* Zona de archivos adjuntos */}
            <div className="col-span-2">
                <FileUpload
                    files={formik.values.documents}
                    onFilesChange={(files) => {
                        formik.setFieldValue('documents', files);
                        formik.setFieldTouched('documents', true);
                    }}
                    title="Archivos adjuntos"
                    maxFiles={10}
                    maxSize={10485760}
                />
                {formik.touched.documents && formik.errors.documents && (
                    <Text className="text-red-500 text-sm mt-1">
                        {formik.errors.documents}
                    </Text>
                )}
            </div>

            {/* Sección de errores generales del formulario */}
            {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
                <div className="col-span-2 p-4 bg-red-50 border border-red-200 rounded-md">
                    <Text className="text-red-700 font-medium mb-2">Por favor, corrija los siguientes errores:</Text>
                    <ul className="list-disc list-inside space-y-1">
                        {Object.entries(formik.errors).map(([field, error]) => (
                            <li key={field} className="text-red-600 text-sm">
                                {error as string}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className='col-span-2 pt-4 text-right'>
                <Button
                    type='submit'
                    disabled={!formik.isValid || formik.isSubmitting}
                    onClick={() => {
                        formik.setFieldTouched('emailSubject', true);
                        formik.setFieldTouched('emailContent', true);
                        formik.setFieldTouched('documents', true);
                        formik.handleSubmit();
                    }}
                >
                    {formik.isSubmitting ? <Loader /> : 'Enviar'}
                </Button>
            </div>
        </div>
    );
};

export default SendEmailForm;
